import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from '../../../utils/axios';
import { useEffect, useState } from 'react';
import { MIconButton } from '../../../components/@material-extend';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import { useSnackbar } from 'notistack';
import { Building } from '../../../@types/model/user';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { LoadingButton } from '@mui/lab';
import { deviceState, getDevices } from 'recoil/deviceState';
import { Device, DeviceParameter } from '../../../@types/model/device';
import '../../../assets/css/deviceGroupsBuildingsDevices.scss';
import BasicModal from 'components/BasicModal';
import DeviceForm from 'components/device/DeviceForm';
import { v4 } from 'uuid';
import BasicModalExpanded from 'components/BasicModalExpanded';
import { ukUAGrid } from '@mui/x-data-grid';

interface BuildingCreateDeviceModalProps {
  buildingCreateDevice: Building | null;
  setBuildingCreateDevice: (building: Building | null) => void;
  deviceTag: string | undefined;
}

const deviceInitState = {
  guid: v4(),
  attributes: {
    name: '',
    shared_by: '',
    is_ref_of: '',
    pointers: [],
    identifier: '',
    workspace: '',
    last_location: null,
    parameters: [],
    device_type_preset: '',
    device_type: '',
    last_data: null,
    state: '',
    tags: [],
    alarm_states: [],
    delivery_configurations: []
  }
};

const BuildingCreateDeviceModal = (props: BuildingCreateDeviceModalProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const devices: Device[] = useRecoilValue(deviceState);
  const setDevices = useSetRecoilState(deviceState);
  const [loading, setLoading] = useState(false);

  const [buildingCreateDeviceLoading, setBuildingCreateDeviceLoading] = useState<boolean>(false);
  const [deviceToCreate, setDeviceToCreate] = useState<Device>({
    ...deviceInitState,
    attributes: {
      ...deviceInitState.attributes,
      tags: [
        props.deviceTag,
        props.buildingCreateDevice?.attributes.is_ref_of ?? props.buildingCreateDevice?.guid
      ]
    }
  } as Device);

  useEffect(() => {
    setDeviceToCreate({
      ...deviceInitState,
      attributes: {
        ...deviceInitState.attributes,
        tags: [
          props.deviceTag,
          props.buildingCreateDevice?.attributes.is_ref_of ?? props.buildingCreateDevice?.guid
        ]
      }
    } as Device);
  }, [props]);

  const createDevice = () => {
    if (deviceToCreate) {
      setLoading(true);
      let newTags = [];
      let guidBuilding = props?.buildingCreateDevice?.guid;
      if (props?.buildingCreateDevice?.attributes.is_ref_of)
        guidBuilding = props?.buildingCreateDevice?.attributes.is_ref_of;
      newTags.push(guidBuilding);
      newTags.push(props.deviceTag);
      let newDevice = {
        guid: v4(),
        name: deviceToCreate.attributes.name,
        state: deviceToCreate.attributes.state,
        identifier: deviceToCreate.attributes.identifier,
        tags: newTags,
        deviceType: deviceToCreate.attributes.device_type_preset,
        last_location: deviceToCreate.attributes.last_location,
        applyAlarms: false, //TODO:
        parameters: deviceToCreate.attributes.parameters.map((dp: DeviceParameter) => {
          return {
            guid: dp.guid,
            identifier: dp.identifier,
            value: dp.value ? dp.value : null
          };
        })
      };
      axios
        .post(`/api/v2/device`, newDevice)
        .then((response: any) => {
          enqueueSnackbar(t('device_added'), {
            variant: 'success',
            action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          setBuildingCreateDeviceLoading(false);
          props.setBuildingCreateDevice(null);
          setDeviceToCreate({ ...deviceInitState });
          setLoading(false);
          const newDevices = [
            {
              guid: newDevice.guid,
              attributes: {
                name: newDevice.name,
                shared_by: '',
                is_ref_of: '',
                pointers: [],
                identifier: newDevice.identifier,
                workspace: '',
                last_location: newDevice.last_location,
                parameters: newDevice.parameters,
                device_type_preset: '',
                device_type: newDevice.deviceType,
                last_data: null,
                state: newDevice.state,
                tags: newDevice.tags,
                alarm_states: [],
                delivery_configurations: []
              }
            } as Device,
            ...devices
          ];
          setDevices(newDevices);
          //   getDevices().then((devices: Device[]) => {
          //     setDevices(devices);
          //   });
        })
        .catch((response) => {
          enqueueSnackbar(response.message, {
            variant: 'error',
            action: (key) => (
              <MIconButton size="small" onClick={() => closeSnackbar(key)}>
                <Icon icon={closeFill} />
              </MIconButton>
            )
          });
          setLoading(false);
        });
    }
  };

  return (
    <BasicModalExpanded
      open={props.buildingCreateDevice !== null}
      handleClose={() => {
        setBuildingCreateDeviceLoading(false);
        props.setBuildingCreateDevice(null);
        setDeviceToCreate({ ...deviceInitState });
      }}
      style={{}}
    >
      <h2 style={{ marginBottom: '10px' }}>{t('create_device')}</h2>
      <DeviceForm
        device={deviceToCreate}
        updateDevice={(device: Device) => setDeviceToCreate({ ...device })}
        isEdit={false}
      />

      <LoadingButton
        onClick={createDevice}
        loading={loading}
        variant={'contained'}
        sx={{ marginTop: '25px' }}
      >
        {t('device_add')}
      </LoadingButton>
    </BasicModalExpanded>
  );
};

export default BuildingCreateDeviceModal;
